import { useEffect, useRef, useState } from "react";
import {
  clearOnboardingHighlightRequest,
  onboardingHighlightEventName,
  readOnboardingHighlightRequest,
  requestOnboardingHighlight,
  type OnboardingHighlightRequest,
} from "@/lib/onboardingHighlight";
import { useLocation } from "react-router-dom";

const TARGET_ATTRIBUTE = "data-onboarding-target";

const clearGuideTargetParams = () => {
  const params = new URLSearchParams(window.location.search);
  const hasGuideParams =
    params.has("guideTarget") ||
    params.has("guidePromptId") ||
    params.has("guideClusterId");
  if (!hasGuideParams) return;
  params.delete("guideTarget");
  params.delete("guidePromptId");
  params.delete("guideClusterId");
  const next = params.toString();
  const nextUrl = `${window.location.pathname}${next ? `?${next}` : ""}${window.location.hash || ""}`;
  window.history.replaceState({}, "", nextUrl);
};

export function OnboardingHighlightManager() {
  const location = useLocation();
  const [request, setRequest] = useState<OnboardingHighlightRequest | null>(null);
  const highlightedElementRef = useRef<HTMLElement | null>(null);
  const highlightedOverlayRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setRequest(readOnboardingHighlightRequest());
    const onRequest = (event: Event) => {
      const customEvent = event as CustomEvent<OnboardingHighlightRequest>;
      if (!customEvent?.detail?.targetId) return;
      setRequest(customEvent.detail);
    };
    window.addEventListener(
      onboardingHighlightEventName,
      onRequest as EventListener,
    );
    return () => {
      window.removeEventListener(
        onboardingHighlightEventName,
        onRequest as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const guideTarget = params.get("guideTarget");
    if (!guideTarget) return;
    requestOnboardingHighlight(guideTarget, 10_000);
    setRequest(readOnboardingHighlightRequest());
  }, [location.search]);

  useEffect(() => {
    if (!request?.targetId) return;
    if (Date.now() > request.expiresAt) {
      clearOnboardingHighlightRequest();
      setRequest(null);
      return;
    }

    let disposed = false;
    let resolveTimer: ReturnType<typeof setTimeout> | null = null;
    let timeoutTimer: ReturnType<typeof setTimeout> | null = null;
    let activeElement: HTMLElement | null = null;

    const isSameRequest = (candidate: OnboardingHighlightRequest | null) => {
      return Boolean(
        candidate &&
          candidate.targetId === request.targetId &&
          candidate.requestedAt === request.requestedAt &&
          candidate.expiresAt === request.expiresAt,
      );
    };

    const teardownHighlight = () => {
      if (disposed) return;
      disposed = true;
      if (resolveTimer) {
        clearTimeout(resolveTimer);
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
      }

      const overlay = document.querySelector(".custom-overlay");
      if (overlay?.parentElement) {
        overlay.parentElement.removeChild(overlay);
      }

      if (activeElement) {
       activeElement.classList.remove("onboarding-guide-highlight-target");
       activeElement.style.zIndex = "";
      }
      highlightedElementRef.current = null;
      window.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };

    const closeHighlight = () => {
      const safariOverlays = (highlightedOverlayRef.current as any)?._safariOverlays as HTMLElement[] | undefined;
      if (safariOverlays) {
        safariOverlays.forEach((el) => el.remove());
        (highlightedOverlayRef.current as any)._safariOverlays = [];
      }

      teardownHighlight();
      const persisted = readOnboardingHighlightRequest();
      if (isSameRequest(persisted)) {
        clearOnboardingHighlightRequest();
        clearGuideTargetParams();
      }
      setRequest((current) => (isSameRequest(current) ? null : current));
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeHighlight();
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      const current = activeElement;
      if (!current) return;
      if (current.contains(event.target as Node)) {
        closeHighlight();
        return;
      }
      closeHighlight();
    };

    const attachOverlay = () => {
      const overlay = document.createElement("div");
      overlay.classList.add("custom-overlay");
      overlay.style.position = "absolute";
      overlay.style.inset = "0";
      overlay.style.width = "100%";
      overlay.style.height = "100%";
      overlay.style.background = "rgba(0, 0, 0, 0.5)";
      overlay.style.zIndex = "999";
      overlay.addEventListener("pointerdown", () => {
        closeHighlight();
      });

      return overlay;
    };

    const attachHighlight = (element: HTMLElement, container: HTMLElement) => {
      if (element.innerText.includes("What to do next (30–60 day plan")) {
        setRequest(null);
        const remaining = Math.max(0, request.expiresAt - Date.now());
        timeoutTimer = setTimeout(closeHighlight, remaining);
        window.addEventListener("keydown", onKeyDown);
        document.addEventListener("pointerdown", onPointerDown, true);
        return;
      }

      activeElement = element;
      highlightedElementRef.current = element;
      element.classList.add("onboarding-guide-highlight-target");
      
      let overlay = container.querySelector(".custom-overlay") as HTMLElement;
      if (!overlay) {
        overlay = attachOverlay();
        container.appendChild(overlay);
      }
      overlay.getBoundingClientRect();

      const rect = element.getBoundingClientRect();
      const padding = 12;
      const radius = 16;
      element.style.position = "relative";
      element.style.zIndex = "1000";
      const x = rect.left - padding;
      const y = rect.top - padding;
      const width = rect.width + padding * 2;
      const height = rect.height + padding * 2;

      overlay.style.clipPath = `
        path("evenodd,
          M0 0
          H ${window.innerWidth}
          V ${window.innerHeight}
          H0 Z

          M ${x + radius} ${y}
          H ${x + width - radius}
          A ${radius} ${radius} 0 0 1 ${x + width} ${y + radius}
          V ${y + height - radius}
          A ${radius} ${radius} 0 0 1 ${x + width - radius} ${y + height}
          H ${x + radius}
          A ${radius} ${radius} 0 0 1 ${x} ${y + height - radius}
          V ${y + radius}
          A ${radius} ${radius} 0 0 1 ${x + radius} ${y}
          Z
        ")
      `;
      if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
        if (overlay.parentElement) overlay.parentElement.removeChild(overlay);

        const padding = 12;
        const rect = highlightedElementRef.current!.getBoundingClientRect();
        const container = document.querySelector(
          '[data-slot="sidebar-inset"]',
        ) as HTMLElement;

        const top = rect.top - padding;
        const left = rect.left - padding;
        const width = rect.width + padding * 2;
        const height = rect.height + padding * 2;

        const overlays: HTMLElement[] = [];

        const createOverlay = (styles: Partial<CSSStyleDeclaration>) => {
          const div = document.createElement("div");
          div.style.position = "fixed";
          div.style.background = "rgba(0,0,0,0.5)";
          div.style.zIndex = "999";
          Object.assign(div.style, styles);
          container.appendChild(div);
          overlays.push(div);
        };

        // Top
        createOverlay({
          top: "0px",
          left: "0px",
          width: "100%",
          height: `${top}px`,
        });
        // Bottom
        createOverlay({
          top: `${top + height}px`,
          left: "0px",
          width: "100%",
          height: `${window.innerHeight - (top + height)}px`,
        });
        // Left
        createOverlay({
          top: `${top}px`,
          left: "0px",
          width: `${left}px`,
          height: `${height}px`,
        });
        // Right
        createOverlay({
          top: `${top}px`,
          left: `${left + width}px`,
          width: `${window.innerWidth - (left + width)}px`,
          height: `${height}px`,
        });

        highlightedOverlayRef.current = container;
        (highlightedOverlayRef.current as any)._safariOverlays = overlays;
      }
      const remaining = Math.max(0, request.expiresAt - Date.now());
      timeoutTimer = setTimeout(closeHighlight, remaining);
      window.addEventListener("keydown", onKeyDown);
      document.addEventListener("pointerdown", onPointerDown, true);
    };

    const tryFindTarget = () => {
      if (disposed) return;
      const selector = `[${TARGET_ATTRIBUTE}="${request.targetId}"]`;
      const element = document.querySelector(selector) as HTMLElement | null;
      const container = document.querySelector('[data-slot="sidebar-inset"]') as HTMLElement | null;

      if (element && container) {
        const TOP_MIN = 96;
        const TOP_MAX = 220;
        const isTargetInView = () => {
          const rect = element.getBoundingClientRect();
          const hasMeasurableRect = rect.width > 0 || rect.height > 0;
          if (!hasMeasurableRect) return false;
          return rect.top >= TOP_MIN && rect.top <= TOP_MAX;
        };

        const settleAndHighlight = (attempt = 0, seenInViewStreak = 0) => {
          if (disposed) return;
          if (Date.now() >= request.expiresAt) {
            closeHighlight();
            return;
          }

          const inView = isTargetInView();
          const nextStreak = inView ? seenInViewStreak + 1 : 0;
          if (nextStreak >= 2) {
            attachHighlight(element, container);
            return;
          }

          if (attempt >= 8) {
            attachHighlight(element, container);
            return;
          }

          element.scrollIntoView({
            behavior: attempt === 0 ? "smooth" : "auto",
            block: "start",
          });
          resolveTimer = setTimeout(() => {
            settleAndHighlight(attempt + 1, nextStreak);
          }, 180);
        };

        if (!isTargetInView()) {
          settleAndHighlight(0, 0);
          return;
        }
        attachHighlight(element, container);
        return;
      }
      if (Date.now() >= request.expiresAt) {
        closeHighlight();
        return;
      }
      resolveTimer = setTimeout(tryFindTarget, 150);
    };

    tryFindTarget();
    return () => {
      teardownHighlight();
    };
  }, [request]);

  return null;
}
